import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BillingTermsCard } from "@/components/supervisor/billing/BillingTermsCard";
import { BillingBalanceCard } from "@/components/supervisor/billing/BillingBalanceCard";
import { InvoicesTable } from "@/components/supervisor/billing/InvoicesTable";
import { PaymentRequestForm } from "@/components/supervisor/billing/PaymentRequestForm";
import { PaymentRequestHistory } from "@/components/supervisor/billing/PaymentRequestHistory";
import {
  useSupervisorBilling,
  useSupervisorPaymentRequests,
} from "@/components/supervisor/billing/use-supervisor-billing";

export const Route = createFileRoute("/dashboard/supervisor/billing")({
  component: SupervisorBillingPage,
});

function SupervisorBillingPage() {
  const { t } = useTranslation();
  const billing = useSupervisorBilling();
  const requests = useSupervisorPaymentRequests();
  const data = billing.data;

  const requestType = data?.billingModel === "invoice" ? "interim_invoice" : "credit_topup";
  const hasPending = (requests.data ?? []).some((r) => r.type === requestType && r.status === "pending");

  return (
    <div className="space-y-5">
      <div>
        <h1
          className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("supervisor.billing.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("supervisor.billing.subtitle")}</p>
      </div>

      {billing.isLoading ? (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="h-56 animate-pulse rounded-xl border bg-card lg:col-span-2" />
          <div className="h-36 animate-pulse rounded-xl border bg-card" />
        </div>
      ) : billing.isError || !data ? (
        <p className="rounded-xl border border-dashed border-border/60 px-5 py-10 text-center text-sm text-muted-foreground">
          {billing.error?.message ?? t("common.errorLoading")}
        </p>
      ) : (
        <>
          <div className="grid items-start gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <BillingTermsCard billing={data} />
            </div>
            <BillingBalanceCard billing={data} />
          </div>

          {/* Firma prebačena na kredit i dalje vidi fakture iz perioda na fakturi. */}
          {(data.billingModel === "invoice" || data.invoices.length > 0) && (
            <InvoicesTable invoices={data.invoices} today={data.today} />
          )}

          <section className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
            <div className="border-b border-border/40 px-5 py-3.5">
              <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {t("supervisor.billing.requests.heading")}
              </h2>
            </div>
            <PaymentRequestForm billingModel={data.billingModel} hasPending={hasPending} />
            <div className="border-t border-border/40">
              <h3 className="px-5 pt-4 text-xs font-semibold text-muted-foreground">
                {t("supervisor.billing.requests.history")}
              </h3>
              <PaymentRequestHistory requests={requests.data ?? []} isLoading={requests.isLoading} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
